import { AuthContext } from '@repo-level/core-front';
import type { IPromoDate } from 'api/cms/cloverGoldPromo';
import { CloverGoldContext } from 'contexts/cloverGold';
import type { USER_GROUP } from 'helpers/user';
import { isNowBetween } from 'modules/common/helpers';
import { useMatchingSection } from 'modules/common/hooks/useMatchingSection';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import {
  CLOVER_GOLD_RESTRICTED_GROUPS,
  isCloverGoldSecondRound,
} from '../helpers';

export const useCloverGoldAvailable = () => {
  const { user } = useContext(AuthContext);
  const { cloverGoldData } = useContext(CloverGoldContext);

  const promoDate = useMatchingSection<IPromoDate>(
    cloverGoldData?.promoDates ?? []
  );

  const isRestrictedForUser = useMemo(
    () =>
      user?.statuses.some((status) =>
        CLOVER_GOLD_RESTRICTED_GROUPS.includes(status.id as USER_GROUP)
      ) ?? false,
    [user]
  );

  const isAvailable = useMemo(() => {
    if (!promoDate || isRestrictedForUser) return false;
    const isFirstRound = isNowBetween(promoDate.startDate, promoDate.endDate);

    const isSecondRound = isNowBetween(
      promoDate.secondRoundStartDate,
      promoDate.secondRoundEndDate
    );

    return isFirstRound || isSecondRound;
  }, [promoDate, isRestrictedForUser]);

  const endDate = useMemo(() => {
    if (!promoDate) return '';
    const rawDate = isCloverGoldSecondRound(promoDate)
      ? promoDate.secondRoundEndDate
      : promoDate.endDate;

    return moment.utc(rawDate).toISOString();
  }, [promoDate]);

  return { isAvailable, endDate };
};
